export type NullableNumber = number | null | undefined;

export interface SubscriptionRow {
  id: number;
  store?: { id?: number; uuid?: string; name?: string };
  group?: { id?: number; name?: string } | null;
  plan?: { id?: number; code?: string; name?: string };
  status?: string;
  currency?: string;
  pricing?: {
    monthly_value_cents?: NullableNumber;
    included_minutes?: NullableNumber;
  };
  current_period?: { start?: string | null; end?: string | null };
  provider?: {
    name?: string;
    customer_id?: string | null;
    subscription_id?: string | null;
    linked?: boolean;
  };
  lifetime_paid_revenue_cents?: NullableNumber;
  fx?: { rate?: string | null; converted?: boolean };
  /** Current-period Retell cost in USD cents per minute. */
  retell_usd_cents_per_minute?: NullableNumber;
  retell?: {
    current_period?: {
      call_count?: NullableNumber;
      duration_minutes?: NullableNumber;
      cost_cents?: NullableNumber;
    };
    lifetime?: { cost_cents?: NullableNumber };
  };
  opentable?: {
    connected?: boolean;
    monthly_cost_cents?: NullableNumber;
    lifetime_cost_cents?: NullableNumber;
  };
  current_cost_cents?: NullableNumber;
  current_margin_percent?: NullableNumber;
  lifetime_cost_cents?: NullableNumber;
}

export interface FxSnapshot {
  as_of?: string | null;
  source?: string | null;
  stale?: boolean;
  rates?: Record<string, string>;
}

export interface SubscriptionsResponse {
  count?: number;
  fx?: FxSnapshot | null;
  results?: SubscriptionRow[];
}

export interface BillingTrend {
  month?: string;
  paid_revenue_cents?: NullableNumber;
  retell_cost_cents?: NullableNumber;
  opentable_cost_cents?: NullableNumber;
  total_cost_cents?: NullableNumber;
  margin_percent?: NullableNumber;
}

export interface BillingSummary {
  mrr_cents?: NullableNumber;
  arr_cents?: NullableNumber;
  arpu_cents?: NullableNumber;
  status_counts?: Record<string, number | null | undefined>;
  outstanding_invoice_cents?: NullableNumber;
  plan_mix?: Array<{
    plan_code?: string;
    plan_name?: string;
    subscription_count?: NullableNumber;
    mrr_cents?: NullableNumber;
  }>;
  last_12_months?: BillingTrend[];
  current?: {
    retell_cost_cents?: NullableNumber;
    opentable_cost_cents?: NullableNumber;
    total_cost_cents?: NullableNumber;
    margin_percent?: NullableNumber;
  };
}

export interface UnbilledUsageRow {
  store?: { id?: number; uuid?: string; name?: string };
  group?: { id?: number; name?: string } | null;
  subscription?: {
    plan_code?: string;
    plan_name?: string;
    status?: string;
  } | null;
  usage?: {
    call_count?: NullableNumber;
    duration_ms?: NullableNumber;
    duration_minutes?: NullableNumber;
    cost_cents?: NullableNumber;
  };
  last_invoice?: {
    created_at?: string | null;
    status?: string;
    total_cents?: NullableNumber;
  } | null;
}

export interface UnbilledUsageResponse {
  count?: number;
  window?: { start?: string | null; end?: string | null };
  results?: UnbilledUsageRow[];
}

export const numberValue = (value: NullableNumber) =>
  typeof value === 'number' && Number.isFinite(value) ? value : 0;

export const formatMoney = (
  cents: NullableNumber,
  currency = 'USD',
  maximumFractionDigits = 0,
) => {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: maximumFractionDigits,
      maximumFractionDigits,
    }).format(numberValue(cents) / 100);
  } catch {
    return `${currency || 'USD'} ${(numberValue(cents) / 100).toFixed(
      maximumFractionDigits,
    )}`;
  }
};

export const formatCompactUsd = (dollars: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(numberValue(dollars));

export const formatPercent = (value: NullableNumber) =>
  value == null || !Number.isFinite(value) ? '—' : `${value.toFixed(1)}%`;

export const formatDate = (value?: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? '—'
    : new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      }).format(date);
};

export const titleCase = (value?: string) =>
  value
    ? value
        .toLowerCase()
        .split('_')
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ')
    : 'Unknown';

export const statusClassName = (status?: string) => {
  switch (status?.toUpperCase()) {
    case 'ACTIVE':
      return 'bg-success-50 text-success-700 dark:bg-success-500/15 dark:text-success-400';
    case 'PAST_DUE':
      return 'bg-warning-50 text-warning-700 dark:bg-warning-500/15 dark:text-warning-400';
    case 'CANCELED':
    case 'UNPAID':
      return 'bg-error-50 text-error-700 dark:bg-error-500/15 dark:text-error-400';
    default:
      return 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300';
  }
};

export const marginClassName = (margin: NullableNumber) => {
  if (margin == null) return 'text-gray-500 dark:text-gray-400';
  if (margin >= 70) return 'text-success-600 dark:text-success-400';
  if (margin >= 40) return 'text-warning-600 dark:text-warning-400';
  return 'text-error-600 dark:text-error-400';
};
